import React, { useEffect, useState } from "react";
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import dayjs from "dayjs";
import { useForm } from "antd/es/form/Form";
import { useNavigate, Link } from "react-router-dom";
import { Col, Form, Input, Row, Select, Button, message, DatePicker, Flex, Spin, Alert } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../../store/AuthContext";
import { getPartners, addReport, clearData } from "./reportSlice";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { CSVLink } from "react-csv";
import { Mapper } from "./Mapper";
import { Preview } from "./Preview";
import { SectionHeader } from "../../components/SectionHeader/SectionHeader";
import { reportMappings } from "../../constants/reportMappings";
import { Partners } from "./Partners";
import { UploadForm } from "./UploadForm";
import classNames from "classnames";
import css from "./Report.module.css"; 

const VALIDATION_THRESHOLD = 0.3;   // >30% of tested values failing = mapping is probably wrong
const MIN_SAMPLE_SIZE = 5;          // don't flag tiny files off a couple bad rows

export const Report = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = useForm();
  
  const [step, setStep] = useState(1);
  const [formValues, setFormValues] = useState(null);
  const [fields, setFields] = useState({});
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [partnerData, setPartnerData] = useState({});
  const [preflightCheck, setPreflightCheck] = useState('');
  const { user } = useAuth(); 
  const { partners, getPartnersInProgress, addReportInProgress } =
	useSelector((state) => state?.report) || {};

  const onSubmit = async () => {
		let mappedRows = [];
	
		const mappingByField = Object.fromEntries(reportMappings.map(m => [m.value, m]));
		const mappedFields = Object.keys(fields).filter(field => fields[field]);
	
		const violations = {};
		const tested = {};
		mappedFields.forEach(f => { violations[f] = 0; tested[f] = 0; });
	
		for (let rowID in rows) {
			const row = rows[rowID];
			if(rowID == 0)
			console.log(row);
			let mappedRow = {
				upload_row: rowID,
				partner: formValues.partner["Customer Name"],
				report_date: formValues.report_date.format("YYYY-MM-DD"),
				raw_data: Object.values(row).join(',')
			};
			for (let field of Object.keys(fields)) {
				const column = fields[field];
				const mapping = mappingByField[field];
	
				if (column === null || column === undefined) {
					mappedRow[field] = null;
					continue;
				}
	
				const rawValue = row[column];
				const trimmed = (rawValue ?? '').toString().trim();
	
	
				if (mapping?.numeric) {
					const num = Number(String(rawValue).replace(/\D/g, ''));
					mappedRow[field] = Number.isNaN(num) ? undefined : num;
					if (Number.isNaN(num)) {
						console.warn(`Unparseable ${field} value "${rawValue}" in column "${column}" - flagging for review`);
					}
				} else {
					mappedRow[field] = trimmed;
				}
				
				// Validate against the field's expected shape, if one is defined
				if (mapping?.pattern && mappedRow[field] !== '') {
					tested[field]++;
					if (!mapping.pattern.test(mappedRow[field])) {
						violations[field]++;
					}
				}

			}
			mappedRows.push(mappedRow);
		}
	
		// If a mapped field is persistently invalid across the file, the column
		// mapping itself is likely wrong - bail out before touching the backend.
		for (const field of mappedFields) {
			const mapping = mappingByField[field];
			if (!mapping?.pattern) continue;
	
			const sampleSize = tested[field];
			if (sampleSize < MIN_SAMPLE_SIZE) continue;
	
			const ratio = violations[field] / sampleSize;
			if (ratio > VALIDATION_THRESHOLD) {
				setPreflightCheck(
					`Column mapped to "${mapping.label}" looks wrong - ${violations[field]} of ${sampleSize} values don't match the expected format. Please re-check the mapping in Step 2.`
				);
				return;
			}
		}
	
		const result = await dispatch(
			addReport({ payload: mappedRows, file: formValues.fileName, user: user.email, mappedFields })
		);
		let from;
		if (result.error) {
			from = '/upload-error';
		} else {
			from = '/uploaded';
		}
		form.resetFields();
		navigate("/report/logs", { state: { from } });
	};
  
  const getStepClass = (currentStep) => {
	  //css.withError,
	let classes = [css.stepItem];  
	if(currentStep == step)
		classes.push(css.current);
	else if(currentStep < step) 
		classes.push(css.past);
	else
		classes.push(css.future);	
		
	return classNames(...classes);
  }
  
  const submitUploadForm = (formData) => {
	setFormValues(formData);
	const { content, headers } = formData.file;
	setPartnerData(formData.partner);
	setRows(content);
	setHeaders(headers);
	setStep(2);
  }
  
  const submitFieldMap = (fields) => {
	setFields(fields);
	setStep(3);
  }
  
  const goBackToMapper = () => {
	setPreflightCheck('');
	setStep(2);
  }


  const rules = [
	{
	  required: true,
	  message: "This field is required",
	},
  ];
  
  const submitReady = Object.keys(fields).length > 0;

  return (
	<>
	  <PageHeader title="Report Uploader" extraClass={css.reportTitle} />
			<Flex className={css.root} vertical>
				<div className={"ibox-holder"}>
					<div className={css.stepsHolder}>
						<div className={getStepClass(1)}> 
							<span className={css.stepNumber}>1</span>
							<div className={css.stepDescription}>
								<h3 className={css.stepTitle}>Step 1: Choose .csv</h3>
								<p className={css.stepText}>Upload csv file from your computer. File must be in .csv format with headers in row 1, and with no total rows at the bottom</p>
							</div>
						</div>
						<div className={getStepClass(2)}>
							<span className={css.stepNumber}>2</span>
							<div className={css.stepDescription}>
								<h3 className={css.stepTitle}>Step 2: Match Headers</h3>
								<p className={css.stepText}>Match the required header columns from the file. The file should contain all the required fields marked with * plus a Sales figure, Stock figure, or both</p>
							</div>
						</div>
						<div className={getStepClass(3)}>
							<span className={css.stepNumber}>3</span>
							<div className={css.stepDescription}>
								<h3 className={css.stepTitle}>Step 3: Upload Your Files</h3>
								<p className={css.stepText}>Click Upload to load your files into the database. If you make a mistake click “View Logs” to delete and entry</p>
							</div>
						</div>
					</div>
					{step == 1 && <UploadForm submitUploadForm={submitUploadForm} /> }
					{step == 3 && <>
					<SectionHeader title="Upload Your Files" />
					<Row gutter={16}>
						<Col xs={24}>
							{preflightCheck && <Alert type="error" description={preflightCheck} action={
								<Button size="small" onClick={()=>goBackToMapper()}>
								  Go Back
								</Button>
							} />}
							{!preflightCheck && <Form.Item
								className={css.buttonsHolder}
							>
								<Button
									size="large"
									rootClassName={css.submitButton}
									onClick={onSubmit}
									type="primary"
								>
									Upload
								</Button> 
								{addReportInProgress && <Spin size="small" />}
							</Form.Item>}
						</Col>
					</Row>
					</>}
				</div>
				{step == 2 && <>
					<Mapper headers={headers} partnerData={partnerData} onSubmit={submitFieldMap} fileName={formValues.file.name} />
					<div className={"ibox-holder dark"}>
						<Preview headers={headers} rows={rows} />
					</div>
				</>}
			</Flex>
	</>
  );
  
};
